import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, Scale, ShoppingCart, CreditCard, Clock, Globe,
  UserCheck, Shield, Lock, Gavel, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AGBPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Content */}
      <main className="container mx-auto max-w-4xl px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Scale className="h-4 w-4" />
            Rechtliches
          </div>
          <h1 className="font-display text-4xl font-bold mb-4">
            Allgemeine Geschäftsbedingungen
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            für Managed Nextcloud Hosting & Domain-Services
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* § 1 Geltungsbereich */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                § 1 Geltungsbereich
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1.</strong> Diese Allgemeinen Geschäftsbedingungen (nachfolgend "AGB") regeln das Vertragsverhältnis 
                zwischen der Host-On Service Provider GmbH (nachfolgend "Provider") und dem Kunden (nachfolgend "Kunde").
              </p>
              <p>
                <strong>2.</strong> Diese AGB gelten ausschließlich. Entgegenstehende oder von diesen AGB abweichende 
                Bedingungen des Kunden werden nicht anerkannt, es sei denn, der Provider hat diesen im Einzelfall 
                ausdrücklich schriftlich zugestimmt.
              </p>
            </CardContent>
          </Card>

          {/* § 2 Vertragsgegenstand */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" />
                § 2 Vertragsgegenstand und Leistungsumfang
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1.</strong> Der Provider stellt dem Kunden Speicherplatz auf serverseitiger Infrastruktur zum 
                Betrieb einer "Managed Nextcloud" zur Verfügung und ermöglicht den Zugriff darauf über das Internet. 
                Zudem bietet der Provider die Registrierung und Verwaltung von Internet-Domains an.
              </p>
              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="font-semibold text-blue-700 dark:text-blue-400 mb-2">
                  <strong>2. Managed Service:</strong>
                </p>
                <p className="text-blue-600 dark:text-blue-500">
                  Der Provider übernimmt die Wartung, Sicherheitsupdates und Pflege des Betriebssystems sowie der 
                  Nextcloud-Basisinstallation gemäß dem separat vereinbarten Service Level Agreement (SLA).
                </p>
              </div>
              <p>
                <strong>3. Verfügbarkeit:</strong> Die garantierte Verfügbarkeit und die Bedingungen ihrer Messung 
                (insbesondere die Maßgeblichkeit der technischen Service-Domain/Notfall-Domain) richten sich nach dem 
                <Link to="/sla" className="text-primary hover:underline ml-1">SLA</Link>, welches wesentlicher Bestandteil 
                dieses Vertrages ist.
              </p>
              <p>
                <strong>4.</strong> Der Provider ist berechtigt, die technische Infrastruktur zu ändern oder zu aktualisieren, 
                sofern dies die vertraglich vereinbarte Leistung nicht wesentlich beeinträchtigt.
              </p>
            </CardContent>
          </Card>

          {/* § 3 Vertragsschluss */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gavel className="h-5 w-5 text-primary" />
                § 3 Vertragsschluss
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1.</strong> Die Darstellung der Dienste auf der Webseite des Providers stellt kein rechtlich 
                bindendes Angebot dar, sondern eine Aufforderung zur Bestellung.
              </p>
              <p>
                <strong>2.</strong> Der Vertrag kommt zustande, sobald der Kunde den Bestellprozess abschließt und der 
                Provider die Bestellung durch eine Auftragsbestätigung per E-Mail annimmt oder dem Kunden die Zugangsdaten 
                zur Verfügung stellt.
              </p>
            </CardContent>
          </Card>

          {/* § 4 Zahlungsbedingungen */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                § 4 Zahlungsbedingungen und Abrechnung (Subscriptions)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1. Abonnement-Modell:</strong> Alle Hosting-Dienste werden als Abonnement (Subscription) abgerechnet. 
                Die Entgelte sind jeweils für den vereinbarten Abrechnungszeitraum im Voraus fällig.
              </p>
              
              <div>
                <p className="font-semibold mb-2"><strong>2. Zahlungsmethoden:</strong></p>
                <p className="mb-2">Der Provider akzeptiert ausschließlich automatische, wiederkehrende Zahlungen über folgende Dienstleister:</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="w-10 h-10 bg-[#0070ba] rounded-lg flex items-center justify-center text-white font-bold text-xs">
                      PP
                    </div>
                    <div>
                      <p className="font-semibold text-sm">PayPal</p>
                      <p className="text-xs text-muted-foreground">Subscription / Händlerabbuchung</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                    <div className="w-10 h-10 bg-[#635bff] rounded-lg flex items-center justify-center text-white font-bold text-xs">
                      S
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Kreditkarte</p>
                      <p className="text-xs text-muted-foreground">via Stripe Recurring Billing</p>
                    </div>
                  </div>
                </div>
              </div>

              <p>
                <strong>3. Einzugsermächtigung:</strong> Mit Abschluss der Bestellung und Eingabe der Zahlungsdaten 
                ermächtigt der Kunde den Provider bzw. die genannten Zahlungsdienstleister, die fälligen Entgelte zum 
                Beginn eines jeden Abrechnungszeitraums automatisch vom hinterlegten Zahlungsmittel einzuziehen.
              </p>
              
              <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">
                      4. Rücklastschriften
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-500">
                      Schlägt ein Einzug fehl (z. B. mangels Deckung oder wegen Widerspruchs), hat der Kunde die 
                      dadurch entstehenden Mehrkosten (Rücklastschriftgebühren der Banken/Payment-Provider) zu tragen.
                    </p>
                  </div>
                </div>
              </div>

              <p>
                <strong>5. Sperrung bei Zahlungsverzug:</strong> Gerät der Kunde mit der Zahlung in Verzug, ist der 
                Provider berechtigt, den Zugang zur Nextcloud-Instanz nach vorheriger Androhung und Fristsetzung 
                vorübergehend zu sperren, bis die Zahlung beglichen ist. Die Zahlungspflicht bleibt auch während 
                der Sperrung bestehen.
              </p>
            </CardContent>
          </Card>

          {/* § 5 Laufzeit und Kündigung */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                § 5 Laufzeit und Kündigung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1.</strong> Die Vertragslaufzeit richtet sich nach dem vom Kunden gewählten Paket 
                (z. B. 1 Monat oder 12 Monate).
              </p>
              <p>
                <strong>2.</strong> Der Vertrag verlängert sich automatisch um die jeweilige Vertragslaufzeit, 
                sofern er nicht fristgerecht gekündigt wird.
              </p>
              <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <p className="font-semibold text-green-700 dark:text-green-400 mb-1">
                  3. Kündigungsfrist
                </p>
                <p className="text-sm text-green-600 dark:text-green-500">
                  Verträge können <strong>jederzeit zum Ende der aktuellen Laufzeit</strong> gekündigt werden. 
                  Die Kündigung kann bequem über das Kundenportal oder in Textform (E-Mail) erfolgen.
                </p>
              </div>
              <p>
                <strong>4.</strong> Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt.
              </p>
            </CardContent>
          </Card>

          {/* § 6 Domains */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                § 6 Domains (Besondere Bedingungen)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1.</strong> Bei der Beschaffung und/oder Pflege von Domains wird der Provider im Verhältnis 
                zwischen dem Kunden und der jeweiligen Vergabestelle (Registry, z. B. DENIC) lediglich als Vermittler tätig.
              </p>
              <p>
                <strong>2.</strong> Der Provider hat keinen Einfluss auf die Domain-Vergabe. Er übernimmt keine Gewähr 
                dafür, dass die für den Kunden beantragten Domains zugeteilt werden und/oder zugeteilte Domains frei 
                von Rechten Dritter sind oder auf Dauer Bestand haben.
              </p>
              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="font-semibold text-blue-700 dark:text-blue-400 mb-1">
                  3. DNS-Konfiguration (Bring Your Own Domain)
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-500">
                  Sofern der Kunde Domains nutzt, die nicht vom Provider verwaltet werden ("Bring Your Own Domain"), 
                  ist der Kunde für die korrekten DNS-Einstellungen verantwortlich (siehe 
                  <Link to="/sla" className="font-semibold hover:underline ml-1">SLA Punkt 2.2</Link>).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* § 7 Pflichten des Kunden */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-primary" />
                § 7 Pflichten des Kunden
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1. Datensicherung:</strong> Obwohl der Provider regelmäßige Backups erstellt (Managed Service), 
                obliegt es dem Kunden, kritische Daten zusätzlich lokal zu sichern.
              </p>
              <p>
                <strong>2. Zugangsdaten:</strong> Der Kunde ist verpflichtet, seine Zugangsdaten (Passwörter) geheim 
                zu halten und vor dem Zugriff Dritter zu schützen.
              </p>
              <div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                <p className="font-semibold text-red-700 dark:text-red-400 mb-1">
                  3. Inhalte
                </p>
                <p className="text-sm text-red-600 dark:text-red-500">
                  Der Kunde verpflichtet sich, keine rechtswidrigen Inhalte in der Nextcloud zu speichern oder zu 
                  verbreiten (z. B. Urheberrechtsverletzungen, extremistische Inhalte, Malware). Der Provider ist 
                  berechtigt, bei einem begründeten Verdacht die Instanz sofort zu sperren.
                </p>
              </div>
              <p>
                <strong>4. Software-Manipulation:</strong> Da es sich um ein Managed Hosting handelt, ist es dem Kunden 
                untersagt, manuell in die Systemdateien oder die Konfiguration des Webservers einzugreifen, sofern dies 
                nicht über die bereitgestellte Benutzeroberfläche vorgesehen ist.
              </p>
            </CardContent>
          </Card>

          {/* § 8 Gewährleistung und Haftung */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                § 8 Gewährleistung und Haftung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1.</strong> Der Provider haftet für Schäden unbeschränkt, soweit die Schadensursache auf 
                Vorsatz oder grober Fahrlässigkeit beruht.
              </p>
              <p>
                <strong>2.</strong> Für leichte Fahrlässigkeit haftet der Provider nur bei Verletzung wesentlicher 
                Vertragspflichten (Kardinalpflichten). In diesem Fall ist die Haftung auf den vertragstypischen, 
                vorhersehbaren Schaden begrenzt.
              </p>
              <p>
                <strong>3.</strong> Die verschuldensunabhängige Haftung für anfängliche Mängel (§ 536a BGB) wird ausgeschlossen.
              </p>
              <p>
                <strong>4.</strong> Für Störungen, die durch Dritte (z. B. Ausfall von Seekabeln, DNS-Probleme externer 
                Provider) oder höhere Gewalt verursacht werden, haftet der Provider nicht.
              </p>
            </CardContent>
          </Card>

          {/* § 9 Datenschutz */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                § 9 Datenschutz und AV-Vertrag
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1.</strong> Der Provider verarbeitet personenbezogene Daten des Kunden zur Vertragsabwicklung. 
                Es gilt die <Link to="/datenschutz" className="text-primary hover:underline">Datenschutzerklärung</Link> des Providers.
              </p>
              <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="font-semibold text-purple-700 dark:text-purple-400 mb-1">
                  2. Auftragsverarbeitung (Art. 28 DSGVO)
                </p>
                <p className="text-sm text-purple-600 dark:text-purple-500">
                  Da der Kunde personenbezogene Daten Dritter in der Nextcloud speichert, handelt der Provider als 
                  Auftragsverarbeiter gemäß Art. 28 DSGVO. Der Kunde verpflichtet sich, den hierfür bereitgestellten 
                  <Link to="/avv" className="font-semibold hover:underline ml-1">Vertrag zur Auftragsverarbeitung (AV-Vertrag)</Link> digital abzuschließen.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* § 10 Schlussbestimmungen */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gavel className="h-5 w-5 text-primary" />
                § 10 Schlussbestimmungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong>1.</strong> Es gilt das Recht der Bundesrepublik Deutschland.
              </p>
              <p>
                <strong>2.</strong> Sollte eine Bestimmung dieser AGB unwirksam sein, so bleibt der Vertrag im Übrigen wirksam.
              </p>
            </CardContent>
          </Card>

          {/* Related Links */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <p className="font-semibold mb-4">Weitere rechtliche Dokumente:</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/sla">
                    <FileText className="mr-2 h-4 w-4" />
                    SLA
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/datenschutz">
                    <Lock className="mr-2 h-4 w-4" />
                    Datenschutz
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/avv">
                    <Shield className="mr-2 h-4 w-4" />
                    AVV
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/impressum">
                    <FileText className="mr-2 h-4 w-4" />
                    Impressum
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center pt-8">
            <p className="text-muted-foreground mb-4">
              Bereit für deine eigene Wolke?
            </p>
            <Button asChild size="lg">
              <Link to="/checkout">
                Jetzt Wolke erstellen
              </Link>
            </Button>
          </div>
        </div>
      </main>

    </div>
  );
};

export default AGBPage;
